import type {FinishedResponseInteractionService, WalkerService} from 'shared/services';
import {Subkind} from 'shared/constants';
import type {Auth, Availability, Document, DocumentAvailability, Fond, Rec} from 'src/typings';
import {defaultIdMapper, getRecordPath, getRecordUrlId} from 'shared/utils/data-service-utils';
import type AvailabilityDataService from '../../../record/availability.data-service';
import type {Observable} from 'rxjs';
import type {RecordClipboardService} from '../../../record/kp-record-clipboard/record-clipboard.service';
import type RecordService from '../../../record/record.service';
import type LoanRequestService from '../../../loan/loan-request.service';
import type LoginService from 'shared/login/login.service';
import type {RecordEditationDataService} from '../../../record-editation/services/record-editation.data-service';

export class KpSearchItemDocumentPresenter {
    public static presenterName = 'kpSearchItemDocumentPresenter';

    public availability: Availability;

    /*@ngInject*/
    constructor(private currentAuth: Auth,
                private availabilityDataService: AvailabilityDataService,
                private recordClipboardService: RecordClipboardService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private recordService: RecordService,
                private loanRequestService: LoanRequestService,
                private recordEditationDataService: RecordEditationDataService,
                private walker: WalkerService,
                private loginService: LoginService) {
    }

    public createRecordLink(record: Rec, group) {
        const recordId = getRecordUrlId(record);
        let link = `${getRecordPath(recordId)}?back=${encodeURIComponent(window.location.href)}`;
        if (group && group.length <= 100) { // 100 is limit due to max request size
            link += `&group=${group.map((item) => defaultIdMapper(item)).join(',')}`;
        }
        return link;
    }

    public getAvailability(document: Document): Promise<DocumentAvailability> {
        return this.availabilityDataService.getByDocumentAndRequester(document, this.currentAuth.activeUser.id);
    }

    public isLibrarian(): boolean {
        return this.currentAuth.role.includes('ROLE_LIBRARIAN');
    }

    public clipboardRecords(): Observable<Rec[]> {
        return this.recordClipboardService.getRecords$();
    }

    public async mergeToThis(document: Document) {
        try {
            await this.recordService.mergeRecordsAndReload(document, this.recordClipboardService.getAll());
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        }
    }

    public async requestDocument(document: Document) {
        try {
            const actionResponse = await this.loanRequestService.requestDocument(document);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        } finally {
            this.loginService.reloadIfChangedButNotReloaded();
        }
    }

    public async createRecordEditation(record: Rec, fond: Fond): Promise<void> {
        const recordEditation = await this.recordEditationDataService.createNewEditation({
            record,
            fond,
            subkind: Subkind.SUBKIND_DOCUMENT
        });

        await this.walker.newSpaPage(`/record-editations/${recordEditation.id}`);
    }
}